import { useEffect, useState } from 'react';

import { Col, Row, Skeleton } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { doGetAccountBalance, accountsSetValues, getTransfers, doGetPositionList } from 'src/actions';
import { FilterBarConstant, NavigatePathConstant } from 'src/constants';
import {
  useAccountBalance,
  usePositions,
  useFindAccountStatus,
  useSubmittedAccountSelector,
  useTransfers,
  useCumulativeOrders,
} from 'src/hooks';
import { MTabs } from 'src/lib';
import { Transfer, TransferStatusLabel, Position, AccountBalanceSummaryView, OrderStatusLabel } from 'src/models';
import { ColorConstant, Spacing } from 'src/styles';
import { mapFilterToGetOrderListQueryParamsDto } from 'src/utils';

import { AccountSummary } from './AccountSummary';
import { NonTradedHoldingsTable } from './NonTradedHoldingsTable';
import { OfferOrdersTable } from './OfferOrdersTable';
import { OrdersTable } from './OrdersTable';
import { SecurityHoldingsTable } from './SecurityHoldingsTable';

export const SummaryPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [balanceSummaryView, setBalanceSummaryView] = useState<AccountBalanceSummaryView>({});
  const [pendingTransfersTotal, setPendingTransfersTotal] = useState<number | typeof NaN>(0);
  const [nonTradedPositionsTotal, setNonTradedPositionsTotal] = useState<number | typeof NaN>(0);
  const [tradedPositionsTotal, setTradedPositionsTotal] = useState<number | typeof NaN>(0);
  const [workingOrdersTotal, setWorkingOrdersTotal] = useState<number | typeof NaN>(0);

  const totalValue = useSelector((state: any) => state.accountDetails?.accountValue?.data?.totalValue);

  const { account } = useSubmittedAccountSelector();
  const {
    cashBalance,
    cashAvailable,
    pendingOfferOrdersTotal,
    isAccountBalanceLoading,
    hasAccountBalanceError,
    accountBalanceError,
  } = useAccountBalance();
  const {
    tradablePositionList,
    nonTradablePositionList,
    isPositionListLoading,
    hasPositionListError,
    positionListError,
  } = usePositions({
    dependency: [account.accountId],
  });
  const { transferList, isTransferListLoading, hasTransferListError, transferListError } = useTransfers({
    dependency: [account.accountId],
  });
  const { cumulativeOrderList, hasCumulativeOrderListError, cumulativeOrderListError } = useCumulativeOrders({
    queryParamsDto: mapFilterToGetOrderListQueryParamsDto({
      filterBy: [`${OrderStatusLabel.WORKING}_${FilterBarConstant.StatusCategory.Order}`],
      timeline: FilterBarConstant.FixedTimeline.ThisYear,
    }),
    dependency: [account.accountId],
  });
  useFindAccountStatus({ dependency: [account.accountId] });

  const findPendingTransfersTotal = () => {
    return transferList
      .filter((transfer: Transfer) => transfer.status.isProcessing || transfer.status.isPending)
      .reduce((total, transfer) => total + (transfer.direction.isDeposit ? transfer.amount : -transfer.amount), 0);
  };

  const findTradedPositionsTotal = () => {
    return tradablePositionList.reduce((acc: number, object: Position) => acc + (object?.totalValue || 0), 0) || 0;
  };

  const findNonTradedPositionsTotal = () => {
    return nonTradablePositionList.reduce((acc: number, object: Position) => acc + (object?.totalValue || 0), 0) || 0;
  };

  const findWorkingOrdersTotal = () => {
    return cumulativeOrderList.reduce((total: number, order) => {
      if (order.action.isBuy) {
        const orderCost = order.isPartial ? -((order.quantity - order.filledQuantity) * order.price) : order.totalCost;

        return total + orderCost;
      }

      return total;
    }, 0);
  };

  const refreshPage = () => {
    dispatch(doGetAccountBalance(account.accountId));
    dispatch(doGetPositionList({ params: { accountId: account.accountId } }));
    dispatch(getTransfers(account.accountId));
  };

  useEffect(() => {
    setPendingTransfersTotal(findPendingTransfersTotal());
  }, [transferList]);

  useEffect(() => {
    setTradedPositionsTotal(findTradedPositionsTotal());
  }, [tradablePositionList]);

  useEffect(() => {
    setNonTradedPositionsTotal(findNonTradedPositionsTotal());
  }, [nonTradablePositionList]);

  useEffect(() => {
    setWorkingOrdersTotal(findWorkingOrdersTotal());
  }, [cumulativeOrderList]);

  useEffect(() => {
    const accountValue = cashBalance + tradedPositionsTotal + nonTradedPositionsTotal;

    dispatch(
      accountsSetValues({
        totalValue: accountValue,
        pendingCashTransactions: pendingTransfersTotal,
        cashAvailable,
      }),
    );
  }, [
    account.accountId,
    cashBalance,
    tradedPositionsTotal,
    nonTradedPositionsTotal,
    pendingTransfersTotal,
    cashAvailable,
  ]);

  useEffect(() => {
    setBalanceSummaryView({
      cashAvailable: {
        value: cashAvailable,
        hasError: hasAccountBalanceError,
        error: accountBalanceError,
        color: ColorConstant.GREEN7,
      },
      pendingTransations: {
        value: pendingTransfersTotal,
        hasError: hasTransferListError,
        error: transferListError,
        onClick: () =>
          navigate(
            `../${NavigatePathConstant.HISTORY}?show=${JSON.stringify([
              FilterBarConstant.Show.DepositTransfer,
              FilterBarConstant.Show.WithdrawalTransfer,
            ])}&filterBy=${JSON.stringify([
              `${TransferStatusLabel.PENDING}_${FilterBarConstant.StatusCategory.MoneyTransfer}`,
              `${TransferStatusLabel.PROCESSING}_${FilterBarConstant.StatusCategory.MoneyTransfer}`,
            ])}`,
          ),
        color: ColorConstant.ORANGE7,
      },
      securities: {
        value: tradedPositionsTotal,
        hasError: hasPositionListError,
        error: positionListError,
        color: ColorConstant.BRAND7,
      },
      nonTradedHoldings: {
        value: nonTradedPositionsTotal,
        hasError: hasPositionListError,
        error: positionListError,
        color: ColorConstant.BRAND9,
      },
      pendingOfferOrders: {
        value: pendingOfferOrdersTotal,
        hasError: hasAccountBalanceError,
        error: accountBalanceError,
        color: ColorConstant.RED7,
      },
      pendingOrders: {
        value: Math.abs(workingOrdersTotal),
        hasError: hasCumulativeOrderListError,
        error: cumulativeOrderListError,
        color: ColorConstant.RED9,
      },
    });
  }, [
    cashAvailable,
    pendingTransfersTotal,
    pendingOfferOrdersTotal,
    workingOrdersTotal,
    tradedPositionsTotal,
    nonTradedPositionsTotal,
    hasAccountBalanceError,
    hasTransferListError,
    hasPositionListError,
    hasCumulativeOrderListError,
  ]);

  return (
    <Skeleton loading={isPositionListLoading || isTransferListLoading || isAccountBalanceLoading}>
      <Row>
        <Col span={24} className={Spacing.my16}>
          <AccountSummary
            title='Summary'
            isLoading={isPositionListLoading || isTransferListLoading || isAccountBalanceLoading}
            accountValue={totalValue}
            balanceSummary={balanceSummaryView}
            onRefresh={refreshPage}
          />
        </Col>
        <Col span={24} className={Spacing.my16}>
          <MTabs
            items={[
              {
                key: 'holdings',
                label: 'Holdings',
                children: (
                  <Row>
                    <Col span={24} id='securities-section' className={Spacing.my16}>
                      <SecurityHoldingsTable />
                    </Col>
                    <Col span={24} id='non-trade-holdings-section' className={Spacing.my16}>
                      <NonTradedHoldingsTable />
                    </Col>
                  </Row>
                ),
              },
              {
                key: 'orders',
                label: 'Orders',
                children: (
                  <Row>
                    <Col span={24} id='orders-section' className={Spacing.my16}>
                      <OrdersTable />
                    </Col>
                    <Col span={24} id='offer-orders-section' className={Spacing.my16}>
                      <OfferOrdersTable />
                    </Col>
                  </Row>
                ),
              },
            ]}
          />
        </Col>
      </Row>
    </Skeleton>
  );
};
